import { useState } from 'react';
import { Button, ComboBox, Flex, Heading, Select, TagBadge, Text, TextField } from '@chatsdet/ui';
import { EnvelopeSimple, MicrosoftTeamsLogo, Plus, SlackLogo, Trash } from '@phosphor-icons/react';
import { useNotificationDeliveryHealth } from '../notifications/hooks/useNotificationDeliveryHealth';
import { useSlackChannels } from '../notifications/hooks/useSlackChannels';
import { useTeamsAvailable, useTeamsChannels } from '../notifications/hooks/useTeamsChannels';
import type { SuiteAlertConfig } from './types';

export type AlertChannel = 'email' | 'slack' | 'teams';
type AlertStep = SuiteAlertConfig['steps'][number];
type AlertOutcome = 'failure' | 'all';

/** A config can hold at most this many destinations (matches the backend's `AlertConfigSchema.max(10)`). */
const MAX_STEPS = 10;

/** A crude but sufficient client-side check — the backend is the source of truth (`z.string().email()`). */
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Slack connectivity + searchable channel options, shared by every alert
 * surface in this module (suite-wide alerts, a schedule's own alerts, the
 * area settings dialog). Query-keyed by the `'suite-alerts'` scope (distinct
 * from the `'notifications'`/`'digests'` scopes the notification settings
 * pages use), so opening several of these editors in one dialog (suite
 * alerts + new-schedule alerts + N existing-schedule rows) fetches Slack
 * channels once, not once per editor.
 */
function useAlertChannelData() {
  const health = useNotificationDeliveryHealth();
  const slackConnected = health.data?.data.slackConnected === true;
  const slackChannels = useSlackChannels('suite-alerts', { enabled: slackConnected });
  const { teamsAvailable, teamsUnknown } = useTeamsAvailable();
  const teamsChannels = useTeamsChannels('suite-alerts', { enabled: teamsAvailable });
  return {
    slackConnected,
    teamsAvailable,
    teamsUnknown,
    teamsChannelOptions: teamsChannels.channelOptions,
    teamsLoading: teamsChannels.isLoading,
    /** True while connection status isn't known yet (still loading) or couldn't be determined (errored) — `slackConnected` reads `false` in both cases, which is NOT the same as "confirmed disconnected". */
    connectionUnknown: health.isLoading || health.isError,
    channelOptions: slackChannels.channelOptions,
    isLoading: slackChannels.isLoading,
  };
}

/** A Slack or Teams destination's display label — the resolved `#channel-name` when it's in `channelOptions`, else the raw id (e.g. a private/archived channel the viewer can't list, or connection status not yet known). */
function destinationLabel(
  destinationId: string,
  channelOptions: Array<{ id: string; label: string }>
): string {
  return channelOptions.find((option) => option.id === destinationId)?.label ?? destinationId;
}

/** The channel `Select`'s items — shared by the area-settings dialog's single-destination fields and this editor's add-row, so the two pickers can't drift. */
function alertChannelItems(slackConnected: boolean, teamsAvailable: boolean) {
  return [
    { id: 'email', label: 'Email' },
    {
      id: 'slack',
      label: 'Slack',
      disabled: !slackConnected,
      description: slackConnected ? undefined : 'Not connected',
    },
    {
      id: 'teams',
      label: 'Microsoft Teams',
      disabled: !teamsAvailable,
      description: teamsAvailable ? undefined : 'Not connected',
    },
  ];
}

/** A `SuiteAlertConfig`'s first (only) step, decomposed into editable fields. */
export function fieldsFromAlertConfig(alertConfig: SuiteAlertConfig): {
  channel: AlertChannel;
  destination: string;
} {
  const first = alertConfig.steps[0];
  return {
    channel: first?.channel ?? 'email',
    destination:
      first?.channel === 'slack'
        ? (first.slackChannelId ?? '')
        : first?.channel === 'teams'
          ? (first.teamsChannelRef ?? '')
          : (first?.recipients ?? []).join(', '),
  };
}

/** The inverse of `fieldsFromAlertConfig`: an empty destination means no alert. */
export function alertConfigFromFields(channel: AlertChannel, destination: string): SuiteAlertConfig {
  // Only email takes a comma-separated list; a Slack/Teams destination is one opaque id.
  const destinations =
    channel === 'email'
      ? destination
          .split(',')
          .map((value) => value.trim())
          .filter(Boolean)
      : [destination.trim()].filter(Boolean);
  return {
    steps:
      destinations.length === 0
        ? []
        : [
            {
              channel,
              on: ['success', 'failure'],
              ...(channel === 'email'
                ? { recipients: destinations }
                : channel === 'slack'
                  ? { slackChannelId: destinations[0] }
                  : { teamsChannelRef: destinations[0] }),
            },
          ],
  };
}

/**
 * Local `{channel, destination}` state seeded from a `SuiteAlertConfig`, plus
 * the conversion back to one — the single source of truth for the area
 * settings dialog's live-controlled, one-outer-Save fields (`AreaSettingsDialog`).
 */
export function useAlertDestinationFields(alertConfig: SuiteAlertConfig) {
  const initial = fieldsFromAlertConfig(alertConfig);
  const [channel, setChannel] = useState<AlertChannel>(initial.channel);
  const [destination, setDestination] = useState(initial.destination);
  return {
    channel,
    setChannel,
    destination,
    setDestination,
    toAlertConfig: () => alertConfigFromFields(channel, destination),
    /** Re-seed both fields from a config — for a caller that persists across opens/closes and must re-hydrate on each open rather than on mount. */
    reset: (config: SuiteAlertConfig) => {
      const fields = fieldsFromAlertConfig(config);
      setChannel(fields.channel);
      setDestination(fields.destination);
    },
  };
}

/**
 * The channel picker + destination field only — no save affordance. Used by
 * the area settings dialog (its own outer "Save") and this file's add-row
 * (its own "Add" button), so the picker can't drift between the two. The
 * Slack destination is a searchable channel picker (never a raw channel id)
 * once Slack is connected for the org.
 *
 * A saved Slack destination must stay visible in every connection state —
 * `AreaSettingsDialog` renders this for an *existing* saved destination, not
 * just a blank add-form:
 * - while connection status is still loading/erroring, the raw/resolved
 *   destination shows in a static (non-editable) field rather than either
 *   the searchable picker (which would need `channelOptions` this org may
 *   not have fetched yet) or a premature "not connected" hint;
 * - once confirmed disconnected, the destination still shows, alongside the
 *   connect hint, instead of disappearing;
 * - once connected, if the saved channel id isn't in `channelOptions` (a
 *   private channel the viewer isn't in, or an archived one), a synthetic
 *   option keeps it selected and visible in the combo box instead of
 *   rendering blank.
 */
export function AlertDestinationFields({
  channel,
  destination,
  onChannelChange,
  onDestinationChange,
  isDisabled,
}: {
  channel: AlertChannel;
  destination: string;
  onChannelChange: (channel: AlertChannel) => void;
  onDestinationChange: (destination: string) => void;
  /** Render read-only — for a config these fields can't fully represent (e.g. more than one step). */
  isDisabled?: boolean;
}) {
  const data = useAlertChannelData();
  const { slackConnected, teamsAvailable } = data;
  const isTeams = channel === 'teams';
  const connected = isTeams ? teamsAvailable : slackConnected;
  const connectionUnknown = isTeams ? data.teamsUnknown : data.connectionUnknown;
  const channelOptions = isTeams ? data.teamsChannelOptions : data.channelOptions;
  const isLoading = isTeams ? data.teamsLoading : data.isLoading;
  const serviceName = isTeams ? 'Teams' : 'Slack';
  const comboItems =
    destination && !channelOptions.some((option) => option.id === destination)
      ? [...channelOptions, { id: destination, label: destination }]
      : channelOptions;

  return (
    <>
      <div className="w-48 max-w-full shrink-0">
        <Select
          aria-label="Alert channel"
          items={alertChannelItems(slackConnected, teamsAvailable)}
          selectedKey={channel}
          onSelectionChange={(key) => {
            // A destination is only meaningful for its own channel (an email list, a Slack id, a Teams ref).
            const next = String(key) as AlertChannel;
            if (next === channel) return;
            onChannelChange(next);
            onDestinationChange('');
          }}
          isDisabled={isDisabled}
        />
      </div>
      <div className="min-w-40 flex-1">
        {channel === 'email' ? (
          <TextField
            aria-label="Alert destination"
            placeholder="Email addresses, comma-separated"
            value={destination}
            onChange={onDestinationChange}
            isDisabled={isDisabled}
          />
        ) : connectionUnknown ? (
          <div
            data-control=""
            className="flex items-center truncate rounded-md border border-border bg-muted/30 px-3 text-sm text-muted-foreground"
          >
            {destination ? destinationLabel(destination, channelOptions) : `Checking ${serviceName} connection…`}
          </div>
        ) : connected ? (
          <ComboBox
            aria-label="Alert destination"
            items={comboItems}
            selectedKey={destination || null}
            onSelectionChange={(key) => onDestinationChange(key ? String(key) : '')}
            placeholder="Search channels…"
            isLoading={isLoading}
            isDisabled={isDisabled}
          />
        ) : (
          <div className="space-y-1">
            <div
              data-control=""
              className="flex items-center truncate rounded-md border border-border bg-muted/30 px-3 text-sm text-muted-foreground"
            >
              {destination ? destinationLabel(destination, channelOptions) : 'No channel selected'}
            </div>
            <Text size="xs" className="text-muted-foreground text-pretty">
              Connect {serviceName} in Settings → Integrations to change this.
            </Text>
          </div>
        )}
      </div>
    </>
  );
}

/** Human label for one alert step's destination — a resolved `#channel-name` for Slack, falling back to the raw id when it isn't (yet) in `channelOptions`. */
function alertStepDestinationLabel(
  step: AlertStep,
  channelOptions: Array<{ id: string; label: string }>
): string {
  if (step.channel === 'slack') {
    return step.slackChannelId ? destinationLabel(step.slackChannelId, channelOptions) : 'Unknown channel';
  }
  if (step.channel === 'teams') {
    return step.teamsChannelRef ? destinationLabel(step.teamsChannelRef, channelOptions) : 'Unknown channel';
  }
  return (step.recipients ?? []).join(', ') || 'No recipients';
}

/** Human label + tag color for one alert step's trigger condition, as a small badge rather than trailing text. */
function alertStepOutcomeBadge(step: AlertStep): { label: string; color: 'blue' | 'gray' } {
  if (step.on.includes('success') && step.on.includes('failure')) {
    return { label: 'On success & failure', color: 'blue' };
  }
  if (step.on.includes('success')) return { label: 'On success', color: 'gray' };
  return { label: 'On failure', color: 'gray' };
}

/** One configured destination — icon, destination (medium weight), outcome badge, remove button aligned right. Same row shape everywhere this editor is used. */
function AlertDestinationRow({
  step,
  channelOptions,
  onRemove,
  isPending,
}: {
  step: AlertStep;
  channelOptions: Array<{ id: string; label: string }>;
  onRemove: () => void;
  isPending: boolean;
}) {
  const badge = alertStepOutcomeBadge(step);
  const label = alertStepDestinationLabel(step, channelOptions);
  return (
    <Flex align="center" gap="sm" className="min-w-0 px-3 py-2.5">
      {step.channel === 'slack' ? (
        <SlackLogo size={16} className="shrink-0 text-muted-foreground" />
      ) : step.channel === 'teams' ? (
        <MicrosoftTeamsLogo size={16} className="shrink-0 text-muted-foreground" />
      ) : (
        <EnvelopeSimple size={16} className="shrink-0 text-muted-foreground" />
      )}
      <Text size="sm" weight="medium" className="min-w-0 flex-1 truncate" title={label}>
        {label}
      </Text>
      <TagBadge name={badge.label} color={badge.color} size="sm" hideDot className="shrink-0" />
      <Button
        variant="icon"
        size="sm"
        iconOnly
        tone="destructive"
        tooltip="Remove alert"
        iconLeft={<Trash size={15} />}
        onPress={onRemove}
        isPending={isPending}
        className="shrink-0"
      />
    </Flex>
  );
}

/** Same row height/padding as a real row, so the list doesn't jump size when the first alert is added. */
function AlertDestinationEmptyRow() {
  return (
    <div className="px-3 py-2.5">
      <Text size="sm" className="text-muted-foreground">
        No alerts configured yet.
      </Text>
    </div>
  );
}

const OUTCOME_OPTIONS = [
  { id: 'failure' as const, label: 'On failure' },
  { id: 'all' as const, label: 'On success & failure' },
];

/** Every email in a comma-separated destination string that fails `EMAIL_PATTERN`, or `null` when all (at least one) pass. */
function invalidEmails(destination: string): string[] | null {
  const emails = destination
    .split(',')
    .map((value) => value.trim())
    .filter(Boolean);
  const invalid = emails.filter((email) => !EMAIL_PATTERN.test(email));
  return invalid.length > 0 ? invalid : null;
}

/**
 * List + add-form editor for a full `SuiteAlertConfig` (any number of
 * destinations, mixing Slack and email). Adding or removing a destination
 * calls `onSave` with the whole updated config and awaits it — the caller
 * decides what "save" means: `ScheduleSuiteModal`'s suite-wide and
 * per-schedule editors persist it right away via `mutateAsync`, while the
 * new-schedule editor wraps a `setState` in a resolved promise. The add
 * form's fields only clear once an add's promise resolves; on rejection
 * (add OR remove) the list-level error state below is set and shown inline.
 * That's necessary, not just extra context: `main.tsx`'s `MutationCache`
 * only toasts an `ApiError` with `userError: true` — a 5xx or network
 * failure toasts nothing, so this inline message is the only surfaced
 * feedback for those.
 *
 * The list and the add-form render as one bordered unit (existing rows, an
 * empty-state row when there are none, then the add-row as a tinted footer)
 * so they read as a single control rather than two disconnected pieces.
 */
export function AlertDestinationEditor({
  title,
  description,
  alertConfig,
  isPending,
  onSave,
  /** Section-level usage (top of its own card) reads better as an h2; nested usage (under another heading) defaults to h3. */
  headingLevel = 3,
}: {
  title: string;
  description: string;
  alertConfig: SuiteAlertConfig;
  isPending: boolean;
  onSave: (alertConfig: SuiteAlertConfig) => Promise<void>;
  headingLevel?: 2 | 3;
}) {
  const [channel, setChannel] = useState<AlertChannel>('email');
  const [destination, setDestination] = useState('');
  const [outcome, setOutcome] = useState<AlertOutcome>('failure');
  /** Set on a failed add OR remove — both mutate the same list, so one error slot covers either. */
  const [listError, setListError] = useState<string | null>(null);
  const data = useAlertChannelData();
  // Teams channel refs and Slack channel ids never collide, so one lookup list serves both.
  const channelOptions = [...data.channelOptions, ...data.teamsChannelOptions];

  const handleChannelChange = (next: AlertChannel) => {
    setChannel(next);
    setListError(null);
  };
  const handleDestinationChange = (next: string) => {
    setDestination(next);
    setListError(null);
  };

  const atLimit = alertConfig.steps.length >= MAX_STEPS;
  // Parsed, not raw: a destination like ", ," is non-empty but parses to zero
  // recipients, which would otherwise enable Add for a click that silently
  // no-ops (`alertConfigFromFields` would build zero steps).
  const canAdd = !atLimit && alertConfigFromFields(channel, destination).steps.length > 0;

  const addStep = async () => {
    if (!canAdd) return;
    setListError(null);

    if (channel === 'email') {
      const invalid = invalidEmails(destination);
      if (invalid) {
        setListError(`Not a valid email: ${invalid.join(', ')}`);
        return;
      }
    }

    const built = alertConfigFromFields(channel, destination).steps[0];
    if (!built) return;
    const on: Array<'success' | 'failure'> = outcome === 'all' ? ['success', 'failure'] : ['failure'];
    const step: AlertStep = { ...built, on };

    try {
      await onSave({ steps: [...alertConfig.steps, step] });
      setChannel('email');
      setDestination('');
      setOutcome('failure');
    } catch (err) {
      setListError(err instanceof Error ? err.message : 'Failed to add alert');
    }
  };

  const removeStep = async (index: number) => {
    setListError(null);
    try {
      await onSave({ steps: alertConfig.steps.filter((_, i) => i !== index) });
    } catch (err) {
      // Not necessarily surfaced elsewhere: the global toast only fires for
      // a user-facing `ApiError`, not a 5xx/network failure. The row stays
      // because `alertConfig` (the caller's prop) didn't change.
      setListError(err instanceof Error ? err.message : 'Failed to remove alert');
    }
  };

  return (
    <div className="space-y-3">
      <div>
        <Heading level={headingLevel}>{title}</Heading>
        <Text size="xs" className="mt-0.5 text-muted-foreground text-pretty">
          {description}
        </Text>
      </div>

      <div className="divide-y divide-border overflow-hidden rounded-lg border border-border bg-background">
        {alertConfig.steps.length > 0 ? (
          alertConfig.steps.map((step, index) => (
            <AlertDestinationRow
              key={`${step.channel}-${index}`}
              step={step}
              channelOptions={channelOptions}
              onRemove={() => void removeStep(index)}
              isPending={isPending}
            />
          ))
        ) : (
          <AlertDestinationEmptyRow />
        )}

        <div className="flex flex-col gap-2 bg-muted/40 p-3">
          <div className="flex flex-wrap items-end gap-2">
            <AlertDestinationFields
              channel={channel}
              destination={destination}
              onChannelChange={handleChannelChange}
              onDestinationChange={handleDestinationChange}
              isDisabled={isPending}
            />
            <div className="w-44 max-w-full shrink-0">
              <Select
                aria-label="Notify on"
                items={OUTCOME_OPTIONS}
                selectedKey={outcome}
                onSelectionChange={(key) => setOutcome(String(key) as AlertOutcome)}
                isDisabled={isPending}
              />
            </div>
            <Button
              variant="secondary"
              iconLeft={<Plus size={16} />}
              isDisabled={!canAdd}
              isPending={isPending}
              onPress={() => void addStep()}
            >
              Add
            </Button>
          </div>
          {listError && (
            <Text size="xs" className="text-destructive text-pretty">
              {listError}
            </Text>
          )}
          {atLimit && (
            <Text size="xs" className="text-muted-foreground text-pretty">
              Up to {MAX_STEPS} alerts per list — remove one to add another.
            </Text>
          )}
        </div>
      </div>
    </div>
  );
}
